// Layer 3 (Transformations): the per-tab KPI tile rows for the five glass
// landings. Recovery / tonnage tiles read the seed series; financial tiles
// read Cost and Planning through GmMoney; shift summaries derive from heat-map rows.

import { gmMoney, gmBudgetTone, gmAmount, gmSignedAmount, gmPnlCaption, GM_CLOSED_PERIOD } from './GmMoney.transform';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { smelterPenaltyLimit } from '@/versions/v1/shared/bunker-hill-smelter-terms';
import { formatPtDelta } from '@/versions/v1/shared/recovery-vocab.derive';
import { DRIVER_TREE_SEED } from '@/versions/v1/shared/bunker-hill-driver-tree-seed';
import { pricedDriverTree } from '@/versions/v1/modules/advisory/DriverTree/Scene.transform';
import {
  exposureHeadline,
  openCaseExposurePerDay,
} from '@/versions/v1/modules/advisory/DriverTree/Scene.transform';
import type { VizTone } from '@/versions/v1/modules/_viz';
import {
  dailySevenDaySeriesDelta,
  sevenDaySeriesDelta,
} from './LandingSeries.transform';
import {
  formatDateOffsetFromCursor,
  MONTH_PRODUCTION_DAYS,
  mtdCumulativeFromSeries,
  mtdPlanRevisionRead,
  mtdCompleteDays,
  nextCompositeDueLabel,
  shiftValueAt,
  lastCompositeStamps,
} from './LandingCursor.transform';
import { equipmentHealthRows } from './MaintenanceTabs.transform';
import { pbInZnTone, agGradeDecimals } from './LimsTable.transform';
import { JUNE_DAILY_PLAN_DST, juneMtdTonnes, juneWtdDays } from '@/versions/v1/shared/june-tonnage.derive';
import { CURRENCY_GLYPH } from '@/versions/v1/shared/money';
import { FMEA_UPDATE_REVIEWS } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import type { FMEAUpdateReview } from '@/versions/v1/schema/case';

/** D20: every role-landing exposure headline reads the one open-case exposure
 *  function and the one headline spelling the driver tree uses. */
function openCaseExposureHeadline(state: SessionState): ReturnType<typeof exposureHeadline> {
  return exposureHeadline(openCaseExposurePerDay(pricedDriverTree(DRIVER_TREE_SEED, state), state.cursor));
}

export type TabKpiTile = {
  id: string;
  label: string;
  value: string;
  /** Optional qualifier set on the figure's baseline (`and rising`). Default none. */
  valueContext?: string;
  /** Optional lead comparison set in 600 ink-secondary before the caption. Default none. */
  captionLead?: string;
  caption: string;
  tone: VizTone;
  provenance?: string;
  provenanceChip?: string;
};

// ---- Metallurgist (cmet) ----------------------------------------------

export function metallurgistLimsKpis(state: SessionState): TabKpiTile[] {
  const stamps = lastCompositeStamps(state, 6);
  const latest = stamps[0];
  const pbGrade = latest ? (shiftValueAt('rs-pb-grade', latest, state) ?? 0) : 0;
  const znGrade = latest ? (shiftValueAt('rs-zn-grade', latest, state) ?? 0) : 0;
  const pbInZn = latest ? (shiftValueAt('rs-spec-zn-pb', latest, state) ?? 0) : 0;
  const pbInZn7d = dailySevenDaySeriesDelta('rs-spec-zn-pb', state);
  const pbInZnDelta = pbInZn7d.delta;
  const ag = latest ? (shiftValueAt('rs-ag-grade', latest, state) ?? 0) : 0;
  const due = nextCompositeDueLabel(state);
  const pbInZnTrend =
    pbInZnDelta > 0.05 ? 'and rising' : pbInZnDelta < -0.05 ? 'and falling' : 'stable';
  return [
    {
      id: 'cmet-lims-1',
      label: 'Pb/Ag con grade · 24 h',
      value: `${pbGrade.toFixed(1)}% Pb`,
      captionLead: 'above 62% min spec',
      caption: ` · Ag ${ag.toFixed(agGradeDecimals())} oz/t`,
      tone: 'ok',
    },
    {
      id: 'cmet-lims-2',
      label: 'Zn con grade · 24 h',
      value: `${znGrade.toFixed(1)}% Zn`,
      captionLead: 'above 55% min spec',
      caption: '',
      tone: 'ok',
    },
    {
      id: 'cmet-lims-3',
      label: 'Pb in Zn con · 7-day trend',
      value: `${pbInZn.toFixed(1)}%`,
      valueContext: pbInZnTrend,
      captionLead: `vs ${smelterPenaltyLimit('Zn', 'Pb').toFixed(1)}% smelter limit`,
      caption: ` · ${formatPtDelta(pbInZnDelta)} over 7 days`,
      tone: pbInZnTone(pbInZn),
    },
    {
      id: 'cmet-lims-4',
      label: 'Next 12 h composite',
      value: due.value,
      caption: due.caption,
      tone: 'info',
    },
  ];
}

/** The Active AI cases view's figure row (polish.P26 comp A): the week's
 *  resolved cases, the FMEA cause codes in play and the resolution time
 *  against the Tier 3 target. The Met-level case count leads the strip
 *  (`ControlMetLanding.transform.ts`), read from the issue spine. */
export function metallurgistCasesKpis(): TabKpiTile[] {
  return [
    {
      id: 'cmet-cases-2',
      label: 'Resolved this week',
      value: '2 cases',
      caption: 'pH probe calibration · SMBS ratio review',
      tone: 'ok',
    },
    {
      id: 'cmet-cases-3',
      label: 'FMEA references active',
      value: '3 cause codes',
      caption: 'ore hardness · lime silo · CuSO4 batch · 2 FMEA sheets',
      tone: 'info',
    },
    {
      id: 'cmet-cases-4',
      label: 'Avg resolution time',
      value: '4.2 h',
      captionLead: 'vs 6 h Tier 3 target',
      caption: ' · YTD rolling',
      tone: 'ok',
    },
  ];
}

// ---- Senior Metallurgist (smet) ----------------------------------------

export function seniorMetTrendKpis(state: SessionState): TabKpiTile[] {
  const zn = sevenDaySeriesDelta('rs-zn-rec', state);
  // Daily series — `rs-milled-day` is a shift-cadence day-shift tonnage and
  // does not reconcile with the t/d the rest of the route prints.
  const ore = dailySevenDaySeriesDelta('rs-ore-td', state);
  const ph = sevenDaySeriesDelta('rs-zn-ph', state);
  const pb = sevenDaySeriesDelta('rs-pb-rec', state);
  const znSign = zn.delta >= 0 ? '+' : '−';
  const oreSign = ore.delta >= 0 ? '+' : '−';
  const pbSign = pb.delta >= 0 ? '+' : '−';
  return [
    {
      id: 'smet-tr-1',
      label: '7-day Zn rec change',
      value: `${znSign}${Math.abs(zn.delta).toFixed(1)} pt`,
      caption: `${zn.first.toFixed(1)}% → ${zn.last.toFixed(1)}% · steepest decline mid-week`,
      tone: zn.delta < -1 ? 'bad' : 'warn',
    },
    {
      id: 'smet-tr-2',
      label: '7-day throughput change',
      value: `${oreSign}${Math.abs(Math.round(ore.delta))} t/day`,
      caption: `${Math.round(ore.first).toLocaleString()} → ${Math.round(ore.last).toLocaleString()} t/day${ore.delta < 0 ? ' · ore hardness mid-week' : ''}`,
      tone: 'warn',
    },
    {
      id: 'smet-tr-3',
      label: 'pH trend',
      value: ph.delta < -0.3 ? 'Declining then flat' : 'Stable',
      caption: `${ph.first.toFixed(1)} → ${ph.last.toFixed(1)} · lime silo event mid-week`,
      tone: ph.delta < -0.3 ? 'warn' : 'neutral',
    },
    {
      id: 'smet-tr-4',
      label: 'Pb recovery',
      value: `Stable ${pbSign}${Math.abs(pb.delta).toFixed(1)} pt`,
      caption: `${pb.first.toFixed(1)} → ${pb.last.toFixed(1)}% · less affected than Zn`,
      // A stable read is not an exception: its dot stays neutral (status walls quiet).
      tone: 'neutral',
    },
  ];
}

/** The Senior metallurgist's diagnostic read: the leading cause with its
 *  confidence and the hypotheses still competing (typed from Paul's §5c; the
 *  diagnostic tiles and the landing's strip cell and hypotheses card read this
 *  one record, polish.P27). */
export const SMET_LEADING_CAUSE = { label: 'Lime silo depletion', confidencePct: 85 } as const;
export const SMET_COMPETING_HYPOTHESES: readonly { a: string; b: string; on: string }[] = [
  { a: 'Ore hardness', b: 'cyclone wear', on: 'P80' },
  { a: 'CuSO4 batch', b: 'pH', on: 'activation' },
];
/** What waits on the Senior metallurgist's seat (`seat-senior-met`): the FMEA
 *  update reviews routed to the seat and not yet reviewed (polish.P27 r1). The
 *  one source for the strip's `FMEA reviews at your seat`, the sign-off card,
 *  the stewardship list and the diagnostic tile below. Pass the session's
 *  reviews (`applyFmeaReviews`) so a review taken in the session leaves it. */
export function seniorMetSeatReviews(reviews: readonly FMEAUpdateReview[] = FMEA_UPDATE_REVIEWS): FMEAUpdateReview[] {
  return reviews.filter((row) => row.reviewStatus === 'pending' && row.reviewSeats.includes('senior-metallurgist'));
}

export function seniorMetDiagKpis(state: SessionState): TabKpiTile[] {
  const exposure = openCaseExposureHeadline(state);
  return [
    {
      id: 'smet-diag-1',
      label: 'Active diagnostic cases',
      value: '6 issues',
      caption: `Open exposure ${exposure.total}/day · ${exposure.split} · ranked by EBITDA`,
      tone: 'bad',
    },
    {
      id: 'smet-diag-2',
      label: 'Highest confidence',
      value: SMET_LEADING_CAUSE.label,
      caption: `${SMET_LEADING_CAUSE.confidencePct}% confidence · primary Zn rec driver`,
      tone: 'info',
    },
    {
      id: 'smet-diag-3',
      label: 'Competing hypotheses',
      value: `${SMET_COMPETING_HYPOTHESES.length + 1} active`,
      caption: SMET_COMPETING_HYPOTHESES.map((row) => `${row.a} vs ${row.b} on ${row.on}`).join(' · '),
      tone: 'warn',
    },
    {
      id: 'smet-diag-4',
      label: 'Met sign-off required',
      value: `${seniorMetSeatReviews().length} items`,
      caption: seniorMetSeatReviews().map((row) => row.id).join(' · '),
      tone: 'ok',
    },
  ];
}

export function seniorMetHealthKpis(): TabKpiTile[] {
  return [];
}

export function seniorMetKbKpis(state: SessionState): TabKpiTile[] {
  return [
    {
      id: 'smet-kb-1',
      label: 'AI suggestions for review',
      value: '3',
      caption: 'TARP tightening, FMEA update, procedure gap',
      tone: 'warn',
    },
    {
      id: 'smet-kb-2',
      label: 'Approved this month',
      value: '2',
      caption: 'AT-150 calibration and CuSO4 SOP v3.2',
      tone: 'ok',
    },
    {
      id: 'smet-kb-3',
      label: 'FMEA rows updated',
      value: '4',
      caption: 'CuSO4 batch, pH probe, ore hardness, lime silo · last 30 days',
      tone: 'info',
    },
    {
      id: 'smet-kb-4',
      label: 'Knowledge base health',
      value: 'Good',
      caption: `Last full review: ${formatDateOffsetFromCursor(state, -90, { includeYear: true })}`,
      tone: 'ok',
    },
  ];
}

// ---- Superintendent (supt) ----------------------------------------------

/** The Superintendent's production-vs-plan figures, read once for the landing's
 * plan view and its strip cell (polish.P25 #712), so the two cannot drift. */
export function superintendentPlanFigures(state: SessionState): {
  wtdOre: number;
  wtdPlan: number;
  mtdActual: number;
  mtdPlan: number;
  mtdPct: number;
  monthPlan: number;
  forecast: number;
  requiredRate: number;
  runRate: number;
  daysRemaining: number;
} {
  // K21 round 2 (#719) / D25: the WTD figure is the plant week's **complete
  // production days** (`juneWtdDays`) — the same six days 2–7 Jun, 10,652 dst,
  // that Metal accounting's `wtdWindow()`, `/cost`, `/meeting` and the rest of
  // this page publish — not the heat map's rolling 14 shifts (12,372 dst, which
  // counted the open 08 Jun night 850 and day 870 as actuals). The plan is the
  // same day count × the shared daily budget constant: 6 × 1,440 = 8,640, never
  // a typed 10,080.
  const wtdDays = juneWtdDays(state);
  const wtdOre = wtdDays.reduce((sum, day) => sum + day.dst, 0);
  const wtdPlan = wtdDays.length * JUNE_DAILY_PLAN_DST;
  const mtd = mtdCumulativeFromSeries(state);
  const mtdActual = mtd.actual[mtd.actual.length - 1] ?? 0;
  const mtdPlan = mtd.plan[mtd.plan.length - 1] ?? 0;
  const mtdPct = mtdPlan > 0 ? ((mtdActual - mtdPlan) / mtdPlan) * 100 : 0;
  // D25: complete production days only; the open day is still to be produced.
  const daysElapsed = mtdCompleteDays(state);
  // One horizon for the route: the same daily plan constant the MTD plan line
  // and the Manager's escalation card read, over the same month length.
  const plan = mtdPlanRevisionRead(state);
  const monthPlan = plan.monthPlan;
  const daysRemaining = Math.max(1, MONTH_PRODUCTION_DAYS - daysElapsed);
  const forecast = plan.forecastTonnes;
  const requiredRate = Math.round((monthPlan - mtdActual) / daysRemaining);
  const runRate = Math.round(mtdActual / Math.max(1, daysElapsed));
  return { wtdOre, wtdPlan, mtdActual, mtdPlan, mtdPct, monthPlan, forecast, requiredRate, runRate, daysRemaining };
}

// ---- Maintenance (maint) -------------------------------------------------

/** The Maintenance landing's critical alerts as one tile — the project home's
 *  Overview row reads it. The count and names are the landing's own ledger
 *  alerts (`equipmentHealthRows`), so the two cannot disagree (polish.P14). */
export function maintenanceEquipKpis(state: SessionState): TabKpiTile[] {
  const alerts = equipmentHealthRows(state).flatMap((row) => (row.alert ? [row.alert] : []));
  return [
    {
      id: 'maint-eq-1',
      label: 'Critical alerts today',
      value: String(alerts.length),
      caption: alerts.map((alert) => (alert.label.startsWith('Tier') ? `${alert.short} ${alert.label}` : alert.short)).join(' · '),
      tone: alerts.length > 0 ? 'bad' : 'ok',
    },
  ];
}

// ---- Manager (mgr) --------------------------------------------------------

export function managerExecKpis(state: SessionState): TabKpiTile[] {
  const exposure = openCaseExposureHeadline(state);
  // One tonnage source, one named window: the complete production days 1–7 Jun
  // at the replay cursor (D25). `/cost`, `/planning` and `/reports` read the
  // same function, so the tile cannot contradict the money pages.
  const tonnage = juneMtdTonnes(state);
  const mtdTonnes = tonnage.tonnesDst;
  const mtdPlan = tonnage.planDst;
  const tonnesDelta = mtdTonnes - mtdPlan;
  const pct = mtdPlan > 0 ? Math.abs((tonnesDelta / mtdPlan) * 100) : 0;
  const direction = tonnesDelta >= 0 ? 'above' : 'below';
  // Same gate as the Metallurgist tonnage tile: ore hardness explains a
  // shortfall, never an above-plan month.
  const oreCause = tonnesDelta < 0 ? ' · ore hardness' : '';
  // The price tile reads the bridge's own price counterfactual — deck vs
  // budget deck — rather than a fixed "at budget" tone.
  const priceEffect = gmMoney().bridge.find((r) => r.id === 'price')?.value ?? 0;
  return [
    {
      id: 'mgr-exec-1',
      label: `Operating EBITDA · ${GM_CLOSED_PERIOD}`,
      value: gmAmount(gmMoney().mtd.ebitda.actual),
      caption: gmPnlCaption('ebitda'),
      tone: gmMoney().mtd.ebitda.tone,
    },
    {
      id: 'mgr-exec-2',
      label: 'MTD production',
      value: `${mtdTonnes.toLocaleString()} dst`,
      caption: `vs plan ${mtdPlan.toLocaleString()} dst · ${pct.toFixed(1)}% ${direction} · ${tonnage.windowLabel}${oreCause}`,
      tone: gmBudgetTone(mtdTonnes, mtdPlan),
    },
    {
      id: 'mgr-exec-3',
      label: 'Active cases',
      value: `${exposure.total}/day`,
      caption: `at stake vs design · ${exposure.split}`,
      tone: 'bad',
    },
    {
      id: 'mgr-exec-4',
      label: 'Metal prices vs budget',
      value: priceEffect === 0 ? 'At budget' : gmSignedAmount(priceEffect),
      caption: `Pb, Zn, Ag · same ${CURRENCY_GLYPH} deck as budget`,
      tone: gmBudgetTone(priceEffect, 0),
    },
  ];
}

export function managerPerfKpis(): TabKpiTile[] {
  const g = gmMoney();
  const f = g.forecast;
  const central = f.scenarios.find(s=>s.name==='Central')!;
  const low = f.scenarios.find(s=>s.name==='Conservative')!;
  const tonnes = (n:number)=>`${Math.round(n).toLocaleString('en-AU')} dst`;
  return [
    {id:'mgr-perf-1',label:'Year 1-to-date tonnes',value:tonnes(g.annual.yearToDateActualSt),caption:`vs budget ${tonnes(g.annual.yearToDateBudgetSt)} · through 07 Jun`,tone:gmBudgetTone(g.annual.yearToDateActualSt,g.annual.yearToDateBudgetSt)},
    {id:'mgr-perf-2',label:'Full-year forecast (central)',value:tonnes(central.fullYearTonnes),caption:`vs budget ${tonnes(f.budget.tonnes)} · +${central.vsBudgetPct.toFixed(1)}%`,tone:gmBudgetTone(central.fullYearTonnes,f.budget.tonnes)},
    {id:'mgr-perf-3',label:'Full-year NSR (central)',value:gmAmount(central.fullYearNsr),caption:`vs budget ${gmAmount(f.budget.nsr)} · Pb, Zn and Ag`,tone:gmBudgetTone(central.fullYearNsr,f.budget.nsr)},
    {id:'mgr-perf-4',label:'Operating EBITDA (conservative)',value:gmAmount(low.ebitda),caption:`vs budget ${gmAmount(f.budget.ebitda)} · central ${gmAmount(central.ebitda)}`,tone:gmBudgetTone(low.ebitda,f.budget.ebitda)},
  ];
}
